import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Heart, Calendar, BookOpen, ArrowLeft, Bell, User, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Footer from '@/components/Footer';

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/favorites');
      setFavorites(response.data.favorites);
    } catch (error) {
      toast.error('Erro ao carregar favoritos');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (callId) => {
    try {
      await api.delete(`/api/favorites/${callId}`);
      setFavorites(favorites.filter(f => f.call_id !== callId));
      toast.success('Removido dos favoritos');
    } catch (error) {
      toast.error('Erro ao remover favorito');
    }
  };

  const getQualisClass = (qualis) => `qualis-${qualis.toLowerCase()}`;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Meus Favoritos</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button
              data-testid="notifications-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/notifications')}
            >
              <Bell className="h-5 w-5" />
            </Button>
            <Button
              data-testid="profile-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/profile')}
            >
              <User className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">Carregando...</div>
        ) : favorites.length === 0 ? (
          <div className="bg-white border border-[#E4E4E7] rounded-md p-12 text-center">
            <Heart className="h-16 w-16 mx-auto text-[#4B4C52] mb-4" />
            <p className="text-[#4B4C52] mb-4">Nenhuma chamada favorita ainda</p>
            <Button onClick={() => navigate('/dashboard')}>Explorar Chamadas</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {favorites.map(call => (
              <div
                key={call.call_id}
                data-testid={`favorite-item-${call.call_id}`}
                className="bg-white border border-[#E4E4E7] rounded-md p-6"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold tracking-tight mb-2">{call.title}</h3>
                    <p className="text-sm text-[#4B4C52] mb-3">{call.journal_name}</p>
                  </div>
                  <Button
                    data-testid={`remove-favorite-btn-${call.call_id}`}
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFavorite(call.call_id)}
                  >
                    <Heart className="h-5 w-5 fill-red-500 text-red-500" />
                  </Button>
                </div>

                <p className="text-sm mb-4">{call.description}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge className={`${getQualisClass(call.qualis_classification)} font-semibold`}>
                    QUALIS {call.qualis_classification}
                  </Badge>
                  <Badge variant="outline">{call.publication_type}</Badge>
                  {call.is_free && (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Gratuito
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-6 text-sm text-[#4B4C52]">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>Prazo: {formatDate(call.deadline)}</span>
                  </div>
                  {call.knowledge_areas && call.knowledge_areas.length > 0 && (
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      <span>{call.knowledge_areas[0]}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <Button
                    data-testid={`view-call-btn-${call.call_id}`}
                    size="sm"
                    className="bg-[#002FA7] hover:bg-[#001f75]"
                    onClick={() => navigate(`/chamada/${call.slug || call.call_id}`)}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Ver chamada completa
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default FavoritesPage;